/**
 * MOCK · CANONICAL (CT-5273) — dashboards published against an entity type.
 *
 * Shapes match what `@aimsos/ui`'s WidgetGrid expects, because those adapter
 * signatures are fixed by the package: a list of `{ dashboard_uuid, name,
 * is_default, is_user_default }`, and a layout of `{ dashboard_uuid, widgets[] }`.
 *
 * Coverage is chosen to exercise the cases the screens branch on:
 *
 *   customers   THREE dashboards, one flagged default — the selector (CT-5274)
 *               has something to switch between
 *   employees   one dashboard — the selector hides itself
 *   vehicles    OVERVIEW + a second module (GARAGE), APPOINTMENTS empty
 */

// One shape, two thin callers — `widget()` and `kpiWidget()` only ever
// differed in `widget_type`, the default size, and whether `custom_fixed_key`
// is set. A third widget shape (this file already has two) should extend
// this, not copy the `instance_uuid`/`layout`/`constraints` boilerplate a
// third time.
const baseWidget = (uuid, { widgetType, x, y, w, h, constraints, cardExtra = {} }) => ({
  instance_uuid: uuid,
  widget_definition_uuid: `${uuid}-def`,
  widget_type: widgetType,
  accepts_global_filters: true,
  layout: { x, y, w, h },
  constraints,
  config: { card: { description: '', header_buttons: [], ...cardExtra } },
});

const widget = (uuid, title, x, y, w = 6, h = 7) =>
  baseWidget(uuid, {
    widgetType: 'STANDARD',
    x,
    y,
    w,
    h,
    constraints: { minH: 5, maxH: 12, minW: 3, maxW: 12 },
    cardExtra: { title },
  });

// `CUSTOM_FIXED` + `custom_fixed_key: 'kpi'` renders through the package's own
// `KpiWidget` (value, trend icon, progress bar) instead of the generic
// `STANDARD` fallback — a plain title-and-bulleted-list `Widget`, which is
// what every widget here rendered as before this. Real dashboards built in
// the Widget Builder come out `CUSTOM_FIXED` too; this seed just names one of
// its three known keys instead of leaving `widget_type` at the type that
// only ever resolves to the fallback renderer.
const kpiWidget = (uuid, title, x, y, w = 4, h = 4) =>
  baseWidget(uuid, {
    widgetType: 'CUSTOM_FIXED',
    x,
    y,
    w,
    h,
    constraints: { minH: 3, maxH: 6, minW: 3, maxW: 6 },
    cardExtra: { title, custom_fixed_key: 'kpi' },
  });

/**
 * Keyed by entity type AND module.
 *
 * A tab's `module` is what selects which dashboards it shows — Overview's whole
 * behaviour is "the dashboard composed of widgets for THIS case", and Garage is
 * the same renderer on a different case. Keying only by entity type would hand
 * Garage the Overview dashboards and make the data-driven claim half true: the
 * bar dynamic, the content not.
 *
 * `vehicles/APPOINTMENTS` is deliberately empty: a tab can exist with nothing
 * published behind it yet, and that renders the empty state rather than an
 * error.
 */
const LISTS = {
  customers: {
    OVERVIEW: [
      { dashboard_uuid: 'dash-cust-main', name: 'Main', is_default: true, is_user_default: false },
      { dashboard_uuid: 'dash-cust-sales', name: 'Sales', is_default: false, is_user_default: false },
      { dashboard_uuid: 'dash-cust-service', name: 'Service', is_default: false, is_user_default: false },
    ],
  },
  companies: {
    OVERVIEW: [
      { dashboard_uuid: 'dash-co-main', name: 'Main', is_default: true, is_user_default: false },
    ],
  },
  employees: {
    OVERVIEW: [
      { dashboard_uuid: 'dash-emp-main', name: 'Main', is_default: true, is_user_default: false },
    ],
  },
  vehicles: {
    OVERVIEW: [
      { dashboard_uuid: 'dash-veh-main', name: 'Main', is_default: true, is_user_default: false },
    ],
    GARAGE: [
      { dashboard_uuid: 'dash-veh-garage', name: 'Service history', is_default: true, is_user_default: false },
    ],
    APPOINTMENTS: [],
  },
  dealerships: {},
};

const LAYOUTS = {
  'dash-cust-main': [
    kpiWidget('w-cust-revenue', 'Revenue', 0, 0),
    kpiWidget('w-cust-tickets', 'Open tickets', 4, 0),
  ],
  'dash-cust-sales': [widget('w-cust-pipeline', 'Pipeline', 0, 0, 12)],
  'dash-cust-service': [widget('w-cust-csat', 'CSAT', 0, 0, 12)],
  'dash-co-main': [
    kpiWidget('w-co-contacts', 'Active contacts', 0, 0),
    kpiWidget('w-co-deals', 'Open deals', 4, 0),
  ],
  'dash-emp-main': [
    kpiWidget('w-emp-shifts', 'Active shifts', 0, 0),
    kpiWidget('w-emp-hours', 'Hours logged', 4, 0),
  ],
  'dash-veh-main': [widget('w-veh-mileage', 'Mileage', 0, 0, 6), widget('w-veh-health', 'Health', 6, 0, 6)],
  'dash-veh-garage': [widget('w-veh-services', 'Services performed', 0, 0, 12)],
};

const buildSeed = () => ({ lists: { ...LISTS }, layouts: { ...LAYOUTS } });

let mockDashboards = buildSeed();

export const getMockDashboardLists = () => mockDashboards.lists;
export const getMockDashboardLayouts = () => mockDashboards.layouts;

/** Test-only: reset the mutable seed between tests within a file. */
export const __resetMockDashboardsForTests = () => {
  mockDashboards = buildSeed();
};
